/**
 * S7 — which lens the Acervo is showing, persisted per case.
 *
 * ⚠ **`DEFAULT_LENS` is a single constant on purpose** — and the reason has now
 * been paid out. The default was CONTESTED (the guide said `categorias`; a
 * relayed ruling said MODALITY), so the axis was built without pre-empting it
 * and the answer was parked in one place. **RESOLVED 2026-08-09 (D-83): the
 * operator confirmed `categorias` DIRECTLY.** The relayed modality ruling is
 * superseded. Settling it cost exactly this one line, which is what the
 * constant existed for; keep it that way rather than inlining the answer into
 * the grouping logic.
 *
 * Persisted per case because it is work-context, not a global preference: a
 * lawyer browsing one case by category and another by type is a normal thing to
 * want, and forcing one answer across every case would be a claim about how
 * they work that nobody made.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";

export type AcervoLens = "tipos" | "categorias";

/** ⚖ Operator-ruled 2026-08-09 (D-83), confirmed directly. Flip HERE only on a
 *  new ruling; nothing else moves. */
export const DEFAULT_LENS: AcervoLens = "categorias";

interface AcervoLensState {
  /** Keyed by the case scope segment. Absent ⇒ `DEFAULT_LENS`. */
  byCase: Record<string, AcervoLens>;
  set: (caseKey: string, lens: AcervoLens) => void;
}

export const useAcervoLensStore = create<AcervoLensState>()(
  persist(
    (set) => ({
      byCase: {},
      set: (caseKey, lens) =>
        set((s) => ({ byCase: { ...s.byCase, [caseKey]: lens } })),
    }),
    {
      name: "lexide:acervo-lens",
      // Explicit allowlist, per this SPA's persist convention — only the map,
      // never the setter.
      partialize: (s) => ({ byCase: s.byCase }),
    },
  ),
);

/** Resolve the active lens for a case. Absent is NOT an error — it is the default. */
export function lensFor(byCase: Record<string, AcervoLens>, caseKey: string): AcervoLens {
  return byCase[caseKey] ?? DEFAULT_LENS;
}
